<template>
  <div
    v-if="isOpened"
    class="datepicker-wrapper-offers options-group"
    :class="isFullScreen ? 'datepicker-wrapper-offers--mobile' : 'datepicker-wrapper-offers--desktop'"
    id="modal-datepicker-offers"
  >
    <div v-if="isFullScreen" class="datepicker--header">
      <h2>{{ translate('offDealsDatesPlaceholderShort') }}</h2>
      <button class="btn" aria-label="Close" @click="emitToggle(false)">
        <i class="icon-cross sm" />
      </button>
    </div>
    <DatePicker
      v-model="dateRange"
      ref="datePicker"
      class="datepicker--body"
      is-inline
      mode="range"
      title-position="left"
      :min-date="minDate"
      :max-date="maxDate"
      :locale="calendarLocale"
      :value="dateRange"
      :rows="$screens({ default: rows })"
      :columns="$screens({ default: cols })"
      :step="1"
    />

    <div v-if="rows < 25" class="w-100 cursor-pointer text-center" @click="next()">
      <i class="icon-arrow-down xs color-primary" />
    </div>

    <div v-if="isFullScreen" class="datepicker--cta">
      <button
        class="btn btn-secondary"
        aria-label="Reset dates"
        :disabled="!start || !end"
        @click="emitClear()"
      >
        <i class="icon-close sm" />
        <span>{{ translate('offDealsClearFilters') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { calendarLocale } from '@/infrastructure/Configuration'
import { DateFactory } from 'rhg-vue-common'

import { setupCalendar, DatePicker } from 'rhg-v-calendar'
import { calendarOptions } from '@/infrastructure/Calendar'
import 'rhg-v-calendar/dist/rhg-v-calendar.css'

setupCalendar(calendarOptions)

export default {
  name: 'DatepickerModal',
  components: {
    DatePicker
  },
  emits: ['toggle', 'clear', 'apply', 'select'],
  props: {
    isOpened: {
      type: Boolean,
      required: false,
      default: false
    },
    isFullScreen: {
      type: Boolean,
      required: false,
      default: false
    },
    start: {
      type: Date,
      required: false
    },
    end: {
      type: Date,
      required: false
    },
    minDate: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      calendarLocale,
      rows: 2, // 2 years + 1 month
      cols: 1,
      fromPage: {
        year: '',
        month: ''
      },
      toPage: {
        year: '',
        month: ''
      },
      maxDate: ''
    }
  },
  watch: {
    isOpened() {
      const bodyEl = document.querySelector('body')
      this.isOpened && this.isFullScreen
        ? bodyEl.classList.add('overflow-hidden')
        : bodyEl.classList.remove('overflow-hidden')
    },
    end(newVal, prevVal) {
      if (this.isOpened && (!prevVal || newVal?.getTime() !== prevVal?.getTime())) {
        this.emitApply()
      }
    }
  },
  mounted() {
    this.setCalendarRange()
  },
  computed: {
    dateRange: {
      get() {
        const start = this.start
        const end = this.end

        return start || end ? { start, end } : null
      },
      set(value) {
        const isSameDay = value ? DateFactory.isSame(value.start, value.end) : null
        const start = value ? value.start : null
        const endDate = value ? value.end : null
        const end = !isSameDay ? endDate : DateFactory.addDays(value.end, 1)

        this.emitSelect({ start, end })
      }
    }
  },
  methods: {
    setCalendarRange() {
      // expected date format "YYYY-MM-DD"
      const [year, month, day] = this.minDate.split('-')

      const maxYear = (+year + 2).toString()

      this.fromPage.year = year
      this.fromPage.month = month

      this.toPage.year = maxYear
      this.toPage.month = month

      this.maxDate = `${maxYear}-${month}-${day}`
    },
    emitToggle(value) {
      this.$emit('toggle', value)
    },
    scrollInCalendarModal() {
      // eslint-disable-next-line no-undef
      $('.datepicker--body').animate(
        {
          // eslint-disable-next-line no-undef
          scrollTop: $('.vc-grid-container').height()
        },
        'slow'
      )
    },
    emitClear() {
      this.$emit('clear')
    },
    emitSelect(payload) {
      this.$emit('select', payload)
    },
    emitApply() {
      this.$emit('apply')
    },
    next() {
      this.rows += 1
      this.scrollInCalendarModal()
    }
  }
}
</script>

<style lang="scss">
.color-primary {
  color: color('primary') !important;
}

#modal-datepicker-offers {
  margin: 0;
  margin-top: 5px;
}

.datepicker {
  &--header {
    color: color('dark-grey');
    padding: 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    border-bottom: 1px solid color('dark-grey');

    h2 {
      margin-bottom: 0;
      font-size: 2rem;
      font-weight: 600;
    }
  }

  &--body {
    overflow-y: scroll;
    padding: 1rem 0.5rem;
    width: 100%;

    // &::-webkit-scrollbar {
    //   display: none;
    // }
  }

  &--cta {
    display: flex;
    width: 100%;
    padding: 1.5rem 3rem;
    gap: 1.5rem;
    background-color: color('grey-soft');

    @media (min-width: media('tablet-l')) {
      flex-direction: column;
    }

    .btn {
      flex: 1;
      border-radius: 100rem;
      border: none;
      margin: 0;
      padding: 1.5rem 2rem;
    }
  }
}

.datepicker-wrapper-offers {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border: none;
  width: 100%;
  overflow: hidden;

  &--desktop {
    position: absolute;
    top: 100%;
    border-radius: 1rem;

    .datepicker--body {
      max-height: 40rem;
    }
  }

  &--mobile {
    position: fixed;
    top: 0;
    border-radius: 0;
    height: 100%;
    margin: 0 !important;

    .datepicker--body {
      height: 100%;
    }
  }

  .vc-w-full {
    width: fit-content;
    margin: 0 auto;
  }

  .vc-title-wrapper {
    pointer-events: none;
  }

  .vc-title {
    font-size: 1.4rem !important;
  }

  .vc-weekday {
    font-size: 1.2rem !important;
  }

  .vc-weeks {
    -ms-grid-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  }

  .vc-header {
    padding: 10px !important;
    height: 4.8rem;
    background-color: transparent;
  }

  .rna-calendar .vc-day-disabled {
    text-decoration: line-through;
  }

  .vc-arrows-container {
    display: none !important;
  }

  .vc-day.in-prev-month,
  .vc-day.in-next-month {
    visibility: hidden;
  }

  .vc-day-layer {
    .vc-day-box-center-center {
      .vc-highlight {
        background-color: white;
      }

      .vc-highlight-base-middle {
        background: color('light-purple') !important;
        border: 0;
      }
    }

    .vc-day-box-left-center .vc-highlight,
    .vc-day-box-right-center .vc-highlight {
      border-width: 0;
      background: color('light-purple') !important;
    }
  }

  .vc-day-content {
    font-weight: 400;
  }

  .vc-day-disabled {
    cursor: not-allowed;
    pointer-events: none;
    font-weight: 400;
  }

  .vc-day-disabled:hover,
  .vc-day-disabled:focus {
    background: none !important;
  }

  .vc-border {
    border-width: 0;
  }

  .vc-popover-content-wrapper {
    transform: translate3d(0, 30px, 0) !important;
  }

  .vc-popover-content-wrapper.vc-calendar-wrapper {
    border-top: 7px solid color('grey-cool');
  }

  [class*='vc-grid-cell-col--'] {
    padding: 0;
  }

  .vc-highlights {
    & ~ .vc-day-disabled {
      text-decoration: none;
      color: color('grey-cool');
      font-weight: 500;
    }
  }

  &--inline {
    .vc-container {
      width: 100%;
    }

    .vc-grid-container {
      -ms-grid-columns: 1fr 1fr;

      .vc-weeks {
        -ms-grid-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
      }
    }

    .date-picker-footer {
      box-shadow: none;
    }
  }

  .vc-highlight--checkout {
    border: none;
  }

  .vc-day-content--checkout.vc-day-disabled {
    color: color('grey-cool');
    text-decoration: none;
    font-weight: 500;
  }
}
</style>
